import React, { useState, useEffect } from "react";
import { CheckCircle, Award, Users, BookOpen } from "lucide-react";
import { contentAPI } from "../services/api";
import { useLanguage } from "../hooks/useLanguage";
import * as LucideIcons from "lucide-react";

const QualityAssurance = () => {
  const currentLanguage = useLanguage();
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const sections = await contentAPI.getSections("home").catch(() => []);
        const qualitySection = sections.find(s => s.section_type === "quality");
        if (qualitySection?.content_data) {
          setSectionData(qualitySection.content_data);
        }
      } catch (error) {
        console.error("Error fetching quality section:", error);
      }
    };
    fetchSectionData();
  }, []);

  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      return value[currentLanguage] || value['de'] || value[Object.keys(value)[0]] || fallback;
    }
    return String(value) || fallback;
  };

  const sectionTitle = sectionData ? getStringValue(sectionData.title, "Qualitätssicherung") : "Qualitätssicherung";
  const sectionDescription = sectionData ? getStringValue(sectionData.description, "Unser Pflegedienst garantiert das Erreichen der höchsten Messlatte, gleichmäßige und konstante Versorgung der betreuten Patienten.") : "Unser Pflegedienst garantiert das Erreichen der höchsten Messlatte, gleichmäßige und konstante Versorgung der betreuten Patienten.";
  const responsibilitiesTitle = sectionData ? getStringValue(sectionData.responsibilities_title, "Aufgaben der leitenden Pflegefachkraft") : "Aufgaben der leitenden Pflegefachkraft";
  const responsibilities = sectionData?.responsibilities || [
    "Fachliche Planung und Überwachung der Pflegeprozesse",
    "Ständige Überwachung der fachgerechten Führung der Pflegedokumentation",
    "Dienst- und Einsatzplanung entsprechend den individuellen Bedürfnissen der Patienten",
    "Fach- und personengerechte Einsetzung des Personals"
  ];
  const defaultQualityMeasures = [
    {
      icon: Users,
      title: "Regelmäßige Dienstberatungen",
      description: "Alle Mitarbeiter nehmen an regelmäßigen Dienstberatungen teil, einschließlich Schulungen zu Pflegestandards und Verhaltensrichtlinien."
    },
    {
      icon: BookOpen,
      title: "Fort- und Weiterbildung",
      description: "Kontinuierliche Fortbildungen bei Dienstberatungen und externe Weiterbildungsmöglichkeiten für alle Mitarbeiter."
    },
    {
      icon: CheckCircle,
      title: "Pflegevisiten",
      description: "Regelmäßige Durchführung von Pflegevisiten durch die verantwortliche Pflegefachkraft zur Prüfung der Anwendung und Einhaltung von Prophylaxen und Pflegestandards."
    },
    {
      icon: Award,
      title: "Fachliche Verantwortung",
      description: "Die Pflege wird unter ständiger Verantwortung einer ausgebildeten Pflegefachkraft mit langjähriger Erfahrung in der häuslichen Pflege durchgeführt."
    }
  ];

  // Use quality measures from API if available
  const apiQualityMeasures = sectionData?.quality_measures || [];
  const qualityMeasures = apiQualityMeasures.length > 0
    ? apiQualityMeasures.map((measure, index) => {
        const iconName = typeof measure.icon === 'string' ? measure.icon : (typeof measure.icon === 'object' ? getStringValue(measure.icon, "CheckCircle") : "CheckCircle");
        const IconComponent = LucideIcons[iconName] || defaultQualityMeasures[index]?.icon || CheckCircle;
        return {
          icon: IconComponent,
          title: getStringValue(measure.title, defaultQualityMeasures[index]?.title || ""),
          description: getStringValue(measure.description, defaultQualityMeasures[index]?.description || "")
        };
      })
    : defaultQualityMeasures;

  return (
    <section id="qualitaet" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-[var(--vortex-bg)]">
      <div className="max-w-7xl mx-auto w-full min-w-0">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-[var(--vortex-text-primary)]">
            {sectionTitle}
          </h2>
          <p className="text-base sm:text-xl text-[var(--vortex-text-secondary)] max-w-3xl mx-auto leading-relaxed">
            {sectionDescription}
          </p>
        </div>

        {/* Quality Measures */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10 sm:mb-16">
          {qualityMeasures.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div
                key={index}
                className="bg-[var(--vortex-surface-alt)] p-4 sm:p-6 rounded-xl border-2 border-[var(--vortex-border)] hover:border-[var(--vortex-accent-primary)] transition-all hover:shadow-lg"
              >
                <div className="w-14 h-14 rounded-full bg-[var(--vortex-accent-primary)]/10 flex items-center justify-center mb-4">
                  <IconComponent className="w-7 h-7 text-[var(--vortex-accent-primary)]" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-[var(--vortex-text-primary)]">
                  {item.title}
                </h3>
                <p className="text-base text-[var(--vortex-text-secondary)] leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Responsibilities Section */}
        {responsibilities.length > 0 && (
          <div className="bg-[var(--vortex-surface-alt)] p-4 sm:p-6 md:p-10 rounded-xl border-2 border-[var(--vortex-border)] mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-[var(--vortex-text-primary)] text-center">
              {responsibilitiesTitle}
            </h3>
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {responsibilities.map((responsibility, index) => (
                <div key={index} className="flex items-start space-x-3 sm:space-x-4 min-w-0">
                  <CheckCircle className="w-6 h-6 text-[var(--vortex-health)] flex-shrink-0 mt-1" />
                  <p className="text-lg text-[var(--vortex-text-primary)] leading-relaxed">
                    {typeof responsibility === 'string' ? responsibility : getStringValue(responsibility)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default QualityAssurance;

